import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import type { PatSnapSuccessResponseData, PsHttp } from '@patsnap/tahiti-core/common/ps-http';
import type { PsHttpChannel } from '@patsnap/tahiti-core';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { assistantApiUrlPrefix } from '@telecom/shared/domain-config';
import { resolveAssistantApiUrlPrefix } from '@/presentation/pages/assistant/apis';

/** Invalid Search 内联幂等启动路径。 */
export const INVALID_SEARCH_LAUNCH_PATH = '/chat/external/invalidity/launch';

/** 专利无效检索会话中的结构化业务参数。 */
export interface InvalidSearchLaunchPayload {
    /** 作为首条用户需求的专利号或公开号。 */
    text: string;
}

/** 专利无效检索内联幂等启动请求。 */
export interface InvalidSearchLaunchRequest {
    /** 调用方生成的幂等请求 ID。 */
    request_id: string;
    /** 会话初始化所需的业务参数。 */
    payload: InvalidSearchLaunchPayload;
    /** 界面语言对应的平台语言标识。 */
    lang: 'CN' | 'EN';
}

/** 专利无效检索内联幂等启动响应。 */
export interface InvalidSearchLaunchResponse {
    /** 原样返回的幂等请求 ID。 */
    request_id: string;
    /** Assistant 会话 ID。 */
    conversation_id: string;
    /** 后端返回的完整会话对象。 */
    conversation: unknown;
    /** Assistant 会话的 WebSocket 地址。 */
    ws_url: string;
    /** 已登记首条用户需求的文本长度。 */
    text_length: number;
    /** 是否已登记待启动状态。 */
    pending_start: true;
    /** 是否复用了相同幂等请求创建的会话。 */
    reused: boolean;
}

/** 专利无效检索后端访问接口。 */
export interface InvalidSearchApi {
    /** 创建专利无效检索 Assistant 会话。 */
    launchConversation(data: InvalidSearchLaunchRequest): Promise<InvalidSearchLaunchResponse>;
}

/** InvalidSearchApi 的 IOC 注入标识符。 */
export const InvalidSearchApi = Symbol('InvalidSearchApi');

/** 专利无效检索接口实现。 */
@injectable()
export class InvalidSearchApiImpl extends PsHttpService implements InvalidSearchApi {
    constructor(httpClient: PsHttp, httpChannelClient: PsHttpChannel) {
        super(httpClient, httpChannelClient);
    }

    get options() {
        return {
            baseURL: resolveAssistantApiUrlPrefix(
                import.meta.env.VITE_ASSISTANT_API_URL,
                assistantApiUrlPrefix,
            ),
            headers: {
                'Content-Type': 'application/json',
                ...(import.meta.env.VITE_EXTERNAL_USER_ID?.trim()
                    ? { 'X-User-Id': import.meta.env.VITE_EXTERNAL_USER_ID.trim() }
                    : {}),
            },
        };
    }

    async launchConversation(data: InvalidSearchLaunchRequest): Promise<InvalidSearchLaunchResponse> {
        const result = await this.post<PatSnapSuccessResponseData<InvalidSearchLaunchResponse>>({
            url: INVALID_SEARCH_LAUNCH_PATH,
            data,
        });
        return result as unknown as InvalidSearchLaunchResponse;
    }
}
